import React, { useState, useEffect } from 'react';
import { documentAPI } from '../../services/api';
import { AdvancedPDFViewer } from '../pdf-viewer';
import './DocumentViewer.css';

interface Document {
    id: string;
    knowledge_base_id: string;
    filename: string;
    file_size: number;
    file_type: string;
    status: string;
    processing_progress: number;
    created_at: string;
}

interface DocumentViewerProps {
    knowledgeBaseId: string;
    knowledgeBaseName: string;
    onClose: () => void;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
    knowledgeBaseId,
    knowledgeBaseName,
    onClose
}) => {
    const [documents, setDocuments] = useState<Document[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [viewingDocument, setViewingDocument] = useState<Document | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<string>('all');

    useEffect(() => {
        loadDocuments();
    }, [knowledgeBaseId]);

    const loadDocuments = async () => {
        try {
            setLoading(true);
            setError('');
            const result = await documentAPI.getKnowledgeBaseDocuments(knowledgeBaseId);
            if (result.success && result.documents) {
                setDocuments(result.documents);
            } else {
                setError(result.error || '加载文档失败');
            }
        } catch (error) {
            console.error('加载文档失败:', error);
            setError('加载文档失败，请稍后再试');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDocument = (document: Document) => {
        setViewingDocument(document);
    };

    const handleCloseViewer = () => {
        setViewingDocument(null);
    };

    const handleDeleteDocument = async (documentId: string) => {
        if (!confirm('确定要删除这个文档吗？删除后无法恢复。')) {
            return;
        }

        try {
            const result = await documentAPI.deleteDocument(documentId);
            if (result.success) {
                // 重新加载文档列表
                loadDocuments();
            } else {
                setError(result.error || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            setError('删除失败，请稍后再试');
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleString('zh-CN');
    };

    const getStatusText = (status: string): string => {
        const statusMap: { [key: string]: string } = {
            'pending': '等待处理',
            'uploaded': '已上传',
            'processing': '处理中',
            'completed': '已完成',
            'failed': '处理失败'
        };
        return statusMap[status] || status;
    };

    const getStatusIcon = (status: string): string => {
        const iconMap: { [key: string]: string } = {
            'pending': '⏳',
            'uploaded': '📄',
            'processing': '🔄',
            'completed': '✅',
            'failed': '❌'
        };
        return iconMap[status] || '📄';
    };

    const getStatusColor = (status: string): string => {
        const colorMap: { [key: string]: string } = {
            'pending': 'text-yellow-600',
            'uploaded': 'text-blue-600',
            'processing': 'text-orange-600',
            'completed': 'text-green-600',
            'failed': 'text-red-600'
        };
        return colorMap[status] || 'text-gray-600';
    };

    const filteredDocuments = selectedStatus === 'all'
        ? documents
        : documents.filter(doc => doc.status === selectedStatus);

    return (
        <div className="document-viewer-overlay" onClick={onClose}>
            <div className="document-viewer-container" onClick={e => e.stopPropagation()}>
                {/* 头部 */}
                <div className="document-viewer-header">
                    <div className="header-content">
                        <h2 className="header-title">
                            📚 {knowledgeBaseName} - 文档管理
                        </h2>
                        <p className="header-subtitle">
                            共 {documents.length} 个文档
                        </p>
                    </div>
                    <button onClick={onClose} className="close-btn">
                        ✕
                    </button>
                </div>

                {/* 筛选器 */}
                <div className="document-filter">
                    <div className="filter-group">
                        <label className="filter-label">状态筛选:</label>
                        <select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            className="filter-select"
                        >
                            <option value="all">全部状态</option>
                            <option value="pending">等待处理</option>
                            <option value="uploaded">已上传</option>
                            <option value="processing">处理中</option>
                            <option value="completed">已完成</option>
                            <option value="failed">处理失败</option>
                        </select>
                    </div>
                    <div className="filter-stats">
                        <span className="stat-item">
                            显示: {filteredDocuments.length} / {documents.length}
                        </span>
                    </div>
                </div>

                {/* 文档列表 */}
                <div className="document-list-container">
                    {loading ? (
                        <div className="loading-container">
                            <div className="loading-spinner">🔄</div>
                            <p>正在加载文档...</p>
                        </div>
                    ) : error ? (
                        <div className="error-container">
                            <div className="error-icon">❌</div>
                            <p>{error}</p>
                            <button onClick={loadDocuments} className="retry-btn">
                                重试
                            </button>
                        </div>
                    ) : filteredDocuments.length === 0 ? (
                        <div className="empty-container">
                            <div className="empty-icon">📭</div>
                            <h3>暂无文档</h3>
                            <p>该知识库还没有任何文档</p>
                        </div>
                    ) : (
                        <div className="document-list">
                            {filteredDocuments.map((doc) => (
                                <div key={doc.id} className="document-item">
                                    <div className="document-icon">
                                        {getStatusIcon(doc.status)}
                                    </div>
                                    <div className="document-info">
                                        <div className="document-name">{doc.filename}</div>
                                        <div className="document-meta">
                                            <span className="meta-item">
                                                📏 大小: {formatFileSize(doc.file_size)}
                                            </span>
                                            <span className={`meta-item status-${doc.status} ${getStatusColor(doc.status)}`}>
                                                🔄 状态: {getStatusText(doc.status)}
                                            </span>
                                            {doc.processing_progress > 0 && (
                                                <span className="meta-item">
                                                    📈 进度: {doc.processing_progress}%
                                                </span>
                                            )}
                                        </div>
                                        <div className="document-date">
                                            📅 创建时间: {formatDate(doc.created_at)}
                                        </div>
                                    </div>
                                    <div className="document-actions">
                                        <button
                                            onClick={() => handleViewDocument(doc)}
                                            className="action-btn view-btn"
                                            title="查看PDF文档"
                                        >
                                            👁️ 查看
                                        </button>
                                        <button
                                            onClick={() => handleDeleteDocument(doc.id)}
                                            className="action-btn delete-btn"
                                            title="删除文档"
                                        >
                                            🗑️ 删除
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* PDF查看器 */}
                {viewingDocument && (
                    <AdvancedPDFViewer
                        documentId={viewingDocument.id}
                        filename={viewingDocument.filename}
                        onClose={handleCloseViewer}
                    />
                )}
            </div>
        </div>
    );
};

export default DocumentViewer;
